/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import moment from 'moment';
import { startOfMonth, endOfMonth } from '@internationalized/date';

import {
    Button,
    Alert,
    Stack,
    Text,
    Icon,
    Box,
    STATUS,
    localToday,
    Spinner,
    useFlags,
} from 'sarsaparilla';
import { calendarDateToMoment, momentToCalendarDate } from 'sarsaparilla/moment-utils';

import DatePicker from '../../components/common/DatePicker';
import {
    fetchTourAvailability,
    resetTourAvailability,
} from '../../actions/tourAvailabilityActions';
import { setDateFilter } from '../../actions/filtersActions';
import { showNoTicketRequired } from '../../utils/launchDarklyUtils';

export function TourCalendarContainer({ openCalendar }) {
    const dispatch = useDispatch();
    const { facilityId, tourId: paramTourId } = useParams();
    const { facilitiesUsingNoTicketRequired } = useFlags();
    const [tourCalendarContainerState, setTourCalendarContainerState] = useState({
        shouldCheckNextDateUpdate: false,
    });
    const accountType = useSelector((state) => state.timedEntry.accountType.accountType);

    const date = useSelector((state) => state.timedEntry.filters.date);
    const availabilityData = useSelector((state) => state.timedEntry.tourAvailability);
    const baseTourId =
        useSelector(
            (state) => state.timedEntry.tours?.tourMap?.[paramTourId]?.base_tour_id
        ) || '';
    const tourId = baseTourId || paramTourId;

    const {
        isAvailableByDate,
        isNotReleasedByDate,
        isSoldOutByDate,
        isFirstComeFirstServedByDate,
        lastFetched,
        error: apiError,
        loadingStatus,
        tourId: reduxTourId,
        farthestAvailability: {
            farthestByTour,
            lastFetched: farthestAvailabilityLastFetched,
        },
        isScheduledByDate,
    } = availabilityData;

    const hasAvailabilityData = useMemo(() => {
        return (
            (loadingStatus === STATUS.SUCCESS ||
                Object.values(isAvailableByDate).length > 0) &&
            !!Object.keys(lastFetched)?.length &&
            !tourCalendarContainerState?.shouldCheckNextDateUpdate
        );
    }, [
        isAvailableByDate,
        lastFetched,
        loadingStatus,
        tourCalendarContainerState?.shouldCheckNextDateUpdate,
    ]);

    const lastReservableDate = useMemo(() => {
        if (!farthestByTour?.[tourId]) {
            return null;
        }

        const lastResDate = moment(farthestByTour?.[tourId], 'YYYY-MM-DD');

        // return null if the last reservable date is in the past
        if (!lastResDate || lastResDate?.isBefore(moment())) {
            return null;
        }

        return momentToCalendarDate(lastResDate);
    }, [farthestByTour, tourId]);

    const calendarDate = useMemo(() => {
        return momentToCalendarDate(date);
    }, [date]);

    const maxValue = useMemo(() => {
        return lastReservableDate
            ? endOfMonth(lastReservableDate)
            : endOfMonth(localToday);
    }, [lastReservableDate]);

    const getDayType = useCallback(
        (day) => {
            if (!hasAvailabilityData) {
                return 'loading';
            }

            if (day && localToday?.compare(day) <= 0) {
                const formattedDay = day.toString();
                const monthKey = formattedDay.slice(0, -3);
                const countForThisDay = isAvailableByDate?.[monthKey]?.[formattedDay];
                const notReleasedCountForThisDay =
                    isNotReleasedByDate?.[monthKey]?.[formattedDay];
                const soldOutForThisDay = isSoldOutByDate?.[monthKey]?.[formattedDay];
                const firstComeFirstServedForThisDay =
                    isFirstComeFirstServedByDate?.[monthKey]?.[formattedDay];
                const hasScheduledInventory =
                    isScheduledByDate?.[monthKey]?.[formattedDay];

                if (
                    countForThisDay === undefined ||
                    notReleasedCountForThisDay === undefined
                ) {
                    return 'loading';
                }

                if (countForThisDay) {
                    return 'available';
                }

                if (notReleasedCountForThisDay) {
                    return 'notYetReleased';
                }

                if (firstComeFirstServedForThisDay) {
                    return 'firstComeFirstServed';
                }

                if (soldOutForThisDay) {
                    return 'soldOut';
                }

                if (
                    showNoTicketRequired(facilityId, facilitiesUsingNoTicketRequired) &&
                    lastReservableDate &&
                    day.compare(lastReservableDate) <= 0 &&
                    !hasScheduledInventory
                ) {
                    return 'noTicketRequired';
                }
            }

            return 'blocked';
        },
        [
            hasAvailabilityData,
            isAvailableByDate,
            isFirstComeFirstServedByDate,
            isNotReleasedByDate,
            isSoldOutByDate,
            facilitiesUsingNoTicketRequired,
            facilityId,
            isScheduledByDate,
            lastReservableDate,
        ]
    );

    const isDateUnavailable = useCallback(
        (day) => {
            if (getDayType(day) === 'blocked' || getDayType(day) === 'soldOut') {
                return true;
            }

            return false;
        },
        [getDayType]
    );

    /**
     * Reset tour availability when the component is destroyed
     */
    useEffect(() => {
        return () => dispatch(resetTourAvailability());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Load availability data
     * If there is no redux data OR the tourId in the availability data doesn't match the URL,
     * we fetch all the data. ALso have to check if the redux availability data tourId is
     * the baseId to make Pearl Harbor work
     */
    useEffect(() => {
        if (tourId !== reduxTourId) {
            dispatch(resetTourAvailability());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduxTourId, tourId]);

    useEffect(() => {
        if (facilityId && tourId && accountType) {
            dispatch(
                fetchTourAvailability({
                    facilityId,
                    tourId,
                    date: date || moment(),
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountType, date, facilityId, tourId]);

    useEffect(() => {
        if (calendarDate && getDayType(calendarDate) === 'blocked') {
            dispatch(setDateFilter(null));
            setTourCalendarContainerState((prevState) => ({
                ...prevState,
                shouldCheckNextDateUpdate: true,
            }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarDate, getDayType]);

    useEffect(() => {
        if (tourCalendarContainerState?.shouldCheckNextDateUpdate) {
            setTourCalendarContainerState((prevState) => ({
                ...prevState,
                shouldCheckNextDateUpdate: false,
            }));
        }
    }, [tourCalendarContainerState?.shouldCheckNextDateUpdate]);

    const onDateChange = (d) => {
        dispatch(setDateFilter(d));
    };

    const handleDateChange = (value, validation) => {
        if (value && !validation.isInvalid) {
            onDateChange(calendarDateToMoment(value));
        } else {
            onDateChange(null);
        }
    };

    // On calendar month change pre-load the adjacent months (previous & next)
    // to the month being switched to
    const onVisibleRangeChange = ({ start }) => {
        const nextMonth = start.add({ months: 1 });
        const prevMonth = start.subtract({ months: 1 });

        // if availability exists for the next month fetch it
        if (nextMonth?.compare(maxValue) <= 0) {
            dispatch(
                fetchTourAvailability({
                    facilityId,
                    tourId,
                    date: calendarDateToMoment(nextMonth),
                })
            );
        }

        // if availability exists for the previous month fetch it
        if (prevMonth?.compare(startOfMonth(localToday)) >= 0) {
            dispatch(
                fetchTourAvailability({
                    facilityId,
                    tourId,
                    date: calendarDateToMoment(prevMonth),
                })
            );
        }
    };

    /**
     * getTooltipContent is deprecated because it is only available to sighted users on
     * desktop using a mouse. Leaving this here in case some PM decides they want the information
     * somewhere else
     */
    // const getTooltipContent = (day) => {
    //     if (day && !isEmpty(isAvailableByDate) && !isEmpty(lastFetched)) {
    //         const formattedDay = day.format('YYYY-MM-DD');
    //         const monthKey = day.format('YYYY-MM');
    //         const countForThisDay = isAvailableByDate?.[monthKey]?.[formattedDay];
    //         const notReleasedCountForThisDay =
    //             isNotReleasedByDate?.[monthKey]?.[formattedDay];

    //         if (
    //             !countForThisDay &&
    //             !!notReleasedCountForThisDay &&
    //             nextReleaseByDate?.[monthKey]?.[formattedDay]
    //         ) {
    //             const releaseDateAndTime = moment(
    //                 nextReleaseByDate?.[monthKey]?.[formattedDay],
    //                 'YYYY-MM-DDTHHmm'
    //             );
    //             return `Next Release: ${releaseDateAndTime.format(
    //                 'll'
    //             )} at ${releaseDateAndTime.format('hh:mm a')} ${releaseDateAndTime
    //                 .tz(timezone)
    //                 .format('z')}`;
    //         }
    //     }
    //     return false;
    // };

    const tryFetchAvailabilityAgain = () => {
        if (facilityId && tourId) {
            dispatch(
                fetchTourAvailability({
                    facilityId,
                    tourId,
                    date: moment(),
                })
            );
        }
    };

    if (!hasAvailabilityData || !farthestAvailabilityLastFetched) {
        return (
            <div className="te-facility-calendar-wrap">
                <div className="te-tour-calendar-loading-indicator">
                    <div>
                        <Spinner size="xs" />
                        <Text fontWeight="semibold">Fetching availability ...</Text>
                    </div>
                </div>
            </div>
        );
    }

    if (apiError) {
        return (
            <Alert type="error" heading="Network error" shouldFocusOnMount>
                <Stack space="sm">
                    <Text>There was a problem getting the tour availability data.</Text>

                    <Box alignX="right">
                        <Button
                            size="xs"
                            appearance="tertiary"
                            iconBeforeElement={<Icon iconName="refresh" />}
                            isLoading={loadingStatus === STATUS.PENDING}
                            onClick={tryFetchAvailabilityAgain}
                        >
                            Try again
                        </Button>
                    </Box>
                </Stack>
            </Alert>
        );
    }

    return (
        <DatePicker
            label="Date"
            defaultValue={calendarDate}
            numVisibleMonths={1}
            onChange={handleDateChange}
            minValue={localToday}
            maxValue={maxValue}
            getDayType={getDayType}
            onVisibleRangeChange={onVisibleRangeChange}
            isDateUnavailable={isDateUnavailable}
            defaultOpen={openCalendar}
        />
    );
}

TourCalendarContainer.propTypes = {
    openCalendar: PropTypes.bool,
};

// cSpell:ignore DDTH
